import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

// ============================================================
// API Client - Configuration from environment variables
// VITE_API_URL must be set in .env
// ============================================================

const API_BASE_URL = import.meta.env.VITE_API_URL;

if (!API_BASE_URL) {
  console.warn(
    '[API Client] VITE_API_URL is not set. API calls will fail. ' +
    'Create .env.local and set VITE_API_URL=https://your-api-domain.com/api/v1'
  );
}

// ---- Axios Instance ----
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL || '/api',
  timeout: 30000,
  withCredentials: true, // Enable cookie-based sessions
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// ---- Request Interceptor: Attach CSRF Token ----
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // We store the CSRF token in memory or localStorage just for the header
    const csrfToken = localStorage.getItem('csrf_token');
    if (csrfToken && config.headers) {
      config.headers['X-CSRF-TOKEN'] = csrfToken;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ---- Response Interceptor: Handle Auth Errors ----
apiClient.interceptors.response.use(
  (response) => {
    // If the server sends a new CSRF token, update it
    if (response.data && response.data.data && response.data.data.csrf_token) {
      localStorage.setItem('csrf_token', response.data.data.csrf_token);
    }
    return response;
  },
  async (error) => {
    // Session expired or Unauthorized
    if (error.response?.status === 401) {
      // Clear client-side flags and redirect to login if not already there
      localStorage.removeItem('csrf_token');
      if (window.location.pathname.startsWith('/admin') && !window.location.pathname.startsWith('/admin/giris')) {
        window.location.href = '/giris';
      }
    }
    return Promise.reject(error);
  }
);

// ---- API Service ----
export const apiService = {
  // ---- Auth ----
  login: (email: string, pass: string) =>
    apiClient.post('/auth/login', { email, password: pass }),
    
  register: (data: Record<string, unknown>) =>
    apiClient.post('/auth/register', data),

  logout: () => apiClient.post('/auth/logout'),

  refreshToken: (refreshToken: string) =>
    apiClient.post('/auth/refresh', { refresh_token: refreshToken }),

  // ---- Products (Storefront) ----
  getProducts: (params?: Record<string, unknown>) =>
    apiClient.get('/products', { params }),

  getProduct: (slug: string) =>
    apiClient.get(`/products/${slug}`),

  getFeaturedProducts: (limit = 12) =>
    apiClient.get('/products/featured', { params: { limit } }),

  getNewArrivals: (limit = 12) =>
    apiClient.get('/products/new-arrivals', { params: { limit } }),

  getBestSellers: (limit = 12) =>
    apiClient.get('/products/best-sellers', { params: { limit } }),

  searchProducts: (q: string, params?: Record<string, unknown>) =>
    apiClient.get('/products/search', { params: { q, ...params } }),

  // ---- Categories (Storefront) ----
  getCategories: () => apiClient.get('/categories'),

  getCategoryProducts: (slug: string, params?: Record<string, unknown>) =>
    apiClient.get(`/categories/${slug}/products`, { params }),

  // ---- Brands (Storefront) ----
  getBrands: () => apiClient.get('/brands'),

  getBrandProducts: (slug: string, params?: Record<string, unknown>) =>
    apiClient.get(`/brands/${slug}/products`, { params }),

  // ---- Orders (Storefront) ----
  createOrder: (data: Record<string, unknown>) =>
    apiClient.post('/orders', data),

  trackOrder: (orderNumber: string) =>
    apiClient.get(`/orders/${orderNumber}`),
    
  uploadReceipt: (data: Record<string, unknown>) =>
    apiClient.post('/receipts', data),

  // ---- Coupons (Storefront) ----
  validateCoupon: (code: string, cartTotal: number) =>
    apiClient.post('/coupons/validate', { code, cart_total: cartTotal }),

  // ---- Banners (Storefront) ----
  getBanners: (position?: string) =>
    apiClient.get('/banners', { params: position ? { position } : undefined }),

  // ---- Blog (Storefront) ----
  getBlogPosts: (page = 1, params?: Record<string, unknown>) =>
    apiClient.get('/blog', { params: { page, ...params } }),

  getBlogPost: (slug: string) =>
    apiClient.get(`/blog/${slug}`),

  // ---- Settings (Storefront) ----
  getPublicSettings: () => apiClient.get('/settings/public'),

  // ---- Shipping (Storefront) ----
  getShippingOptions: () => apiClient.get('/shipping/options'),

  // ---- Bank Accounts (Storefront) ----
  getBankAccounts: () => apiClient.get('/bank-accounts'),

  // ---- Reviews (Storefront) ----
  getProductReviews: (productId: number, params?: Record<string, unknown>) =>
    apiClient.get(`/products/${productId}/reviews`, { params }),

  submitReview: (productId: number, data: Record<string, unknown>) =>
    apiClient.post(`/products/${productId}/reviews`, data),

  // ============================================================
  // Admin Endpoints
  // ============================================================

  // Admin - Dashboard & Analytics
  getDashboard: () => apiClient.get('/admin/dashboard'),
  getAnalytics: (period = 30) =>
    apiClient.get('/admin/analytics', { params: { period } }),

  // Admin - Products
  adminGetProducts: (params?: Record<string, unknown>) =>
    apiClient.get('/admin/products', { params }),
  adminGetProduct: (id: number) =>
    apiClient.get(`/admin/products/${id}`),
  adminCreateProduct: (data: Record<string, unknown>) =>
    apiClient.post('/admin/products', data),
  adminUpdateProduct: (id: number, data: Record<string, unknown>) =>
    apiClient.put(`/admin/products/${id}`, data),
  adminDeleteProduct: (id: number) =>
    apiClient.delete(`/admin/products/${id}`),

  // Admin - Product Images
  uploadProductImage: (productId: number, formData: FormData) =>
    apiClient.post(`/admin/products/${productId}/images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  deleteProductImage: (productId: number, imageId: number) =>
    apiClient.delete(`/admin/products/${productId}/images/${imageId}`),
  reorderProductImages: (productId: number, ids: number[]) =>
    apiClient.patch(`/admin/products/${productId}/images/reorder`, { ids }),
  fetchProductImages: (productId: number) =>
    apiClient.post(`/admin/products/${productId}/images/fetch`),

  // Admin - Categories
  adminGetCategories: (params?: Record<string, unknown>) =>
    apiClient.get('/admin/categories', { params }),
  adminCreateCategory: (data: Record<string, unknown>) =>
    apiClient.post('/admin/categories', data),
  adminUpdateCategory: (id: number, data: Record<string, unknown>) =>
    apiClient.put(`/admin/categories/${id}`, data),
  adminDeleteCategory: (id: number) =>
    apiClient.delete(`/admin/categories/${id}`),

  // Admin - Brands
  adminGetBrands: (params?: Record<string, unknown>) =>
    apiClient.get('/admin/brands', { params }),
  adminCreateBrand: (data: Record<string, unknown>) =>
    apiClient.post('/admin/brands', data),
  adminUpdateBrand: (id: number, data: Record<string, unknown>) =>
    apiClient.put(`/admin/brands/${id}`, data),
  adminDeleteBrand: (id: number) =>
    apiClient.delete(`/admin/brands/${id}`),

  // Admin - Orders
  adminGetOrders: (params?: Record<string, unknown>) =>
    apiClient.get('/admin/orders', { params }),
  adminGetOrder: (id: number) =>
    apiClient.get(`/admin/orders/${id}`),
  adminUpdateOrderStatus: (id: number, status: string) =>
    apiClient.patch(`/admin/orders/${id}/status`, { status }),

  // Admin - Customers / Users
  adminGetCustomers: (params?: Record<string, unknown>) =>
    apiClient.get('/admin/customers', { params }),
  adminGetCustomer: (id: number) =>
    apiClient.get(`/admin/customers/${id}`),
  adminUpdateCustomer: (id: number, data: Record<string, unknown>) =>
    apiClient.put(`/admin/customers/${id}`, data),
  adminDeleteCustomer: (id: number) =>
    apiClient.delete(`/admin/customers/${id}`),

  // Admin - Receipts
  adminGetReceipts: () =>
    apiClient.get('/admin/receipts'),
  adminDeleteReceipt: (id: number) =>
    apiClient.delete(`/admin/receipts/${id}`),

  // Admin - Coupons
  adminGetCoupons: (params?: Record<string, unknown>) =>
    apiClient.get('/admin/coupons', { params }),
  adminCreateCoupon: (data: Record<string, unknown>) =>
    apiClient.post('/admin/coupons', data),
  adminUpdateCoupon: (id: number, data: Record<string, unknown>) =>
    apiClient.put(`/admin/coupons/${id}`, data),
  adminDeleteCoupon: (id: number) =>
    apiClient.delete(`/admin/coupons/${id}`),

  // Admin - Banners
  adminGetBanners: (params?: Record<string, unknown>) =>
    apiClient.get('/admin/banners', { params }),
  adminCreateBanner: (data: Record<string, unknown> | FormData) =>
    apiClient.post('/admin/banners', data, {
      headers: data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {},
    }),
  adminUpdateBanner: (id: number, data: Record<string, unknown> | FormData) =>
    apiClient.put(`/admin/banners/${id}`, data, {
      headers: data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {},
    }),
  adminDeleteBanner: (id: number) =>
    apiClient.delete(`/admin/banners/${id}`),

  // Admin - Blog
  adminGetBlog: (params?: Record<string, unknown>) =>
    apiClient.get('/admin/blog', { params }),
  adminGetBlogPost: (id: number) =>
    apiClient.get(`/admin/blog/${id}`),
  adminCreateBlog: (data: Record<string, unknown> | FormData) =>
    apiClient.post('/admin/blog', data, {
      headers: data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {},
    }),
  adminUpdateBlog: (id: number, data: Record<string, unknown> | FormData) =>
    apiClient.put(`/admin/blog/${id}`, data, {
      headers: data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {},
    }),
  adminDeleteBlog: (id: number) =>
    apiClient.delete(`/admin/blog/${id}`),

  // Admin - Settings
  adminGetSettings: () => apiClient.get('/admin/settings'),
  adminUpdateSettings: (data: Record<string, unknown>) =>
    apiClient.post('/admin/settings', data),

  // Admin - Shipping
  adminGetShipping: (params?: Record<string, unknown>) =>
    apiClient.get('/admin/shipping', { params }),
  adminCreateShipping: (data: Record<string, unknown>) =>
    apiClient.post('/admin/shipping', data),
  adminUpdateShipping: (id: number, data: Record<string, unknown>) =>
    apiClient.put(`/admin/shipping/${id}`, data),
  adminDeleteShipping: (id: number) =>
    apiClient.delete(`/admin/shipping/${id}`),

  // Admin - Reviews
  adminGetReviews: (params?: Record<string, unknown>) =>
    apiClient.get('/admin/reviews', { params }),
  adminApproveReview: (id: number) =>
    apiClient.patch(`/admin/reviews/${id}/approve`),
  adminDeleteReview: (id: number) =>
    apiClient.delete(`/admin/reviews/${id}`),

  // Admin - Media Library
  getMedia: (params?: Record<string, unknown>) =>
    apiClient.get('/admin/media', { params }),
  uploadMedia: (formData: FormData) =>
    apiClient.post('/admin/media/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  deleteMedia: (id: number) =>
    apiClient.delete(`/admin/media/${id}`),

  // Admin - Activity Logs
  adminGetActivityLogs: (params?: Record<string, unknown>) =>
    apiClient.get('/admin/activity-logs', { params }),

  // Admin - Notifications
  adminGetNotifications: (params?: Record<string, unknown>) =>
    apiClient.get('/admin/notifications', { params }),
  adminMarkNotificationRead: (id: number) =>
    apiClient.patch(`/admin/notifications/${id}/read`),
  adminMarkAllNotificationsRead: () =>
    apiClient.post('/admin/notifications/read-all'),

  // Admin - Analytics (detailed)
  adminGetAnalytics: (params?: Record<string, unknown>) =>
    apiClient.get('/admin/analytics', { params }),

  // Admin - Payments
  adminGetPayments: (params?: Record<string, unknown>) =>
    apiClient.get('/admin/payments', { params }),

  // Admin - Invoices
  adminGetInvoices: (params?: Record<string, unknown>) =>
    apiClient.get('/admin/invoices', { params }),

  // Admin - SEO
  adminGetSeo: () =>
    apiClient.get('/admin/seo'),
  adminCreateSeo: (data: Record<string, unknown>) =>
    apiClient.post('/admin/seo', data),
  adminUpdateSeo: (id: number, data: Record<string, unknown>) =>
    apiClient.put(`/admin/seo/${id}`, data),
  adminDeleteSeo: (id: number) =>
    apiClient.delete(`/admin/seo/${id}`),

  // Admin - Import / Export
  adminImportData: (formData: FormData, entity: string, format: string) =>
    apiClient.post(`/admin/import?entity=${entity}&format=${format}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  adminExportData: (entity: string, format: string) =>
    `${API_BASE_URL || '/api'}/admin/export/${entity}?format=${format}`,

  // Admin - Campaigns
  adminGetCampaigns: (params?: Record<string, unknown>) =>
    apiClient.get('/admin/campaigns', { params }),
  adminGetCampaign: (id: number) =>
    apiClient.get(`/admin/campaigns/${id}`),
  adminCreateCampaign: (data: Record<string, unknown>) =>
    apiClient.post('/admin/campaigns', data),
  adminUpdateCampaign: (id: number, data: Record<string, unknown>) =>
    apiClient.put(`/admin/campaigns/${id}`, data),
  adminDeleteCampaign: (id: number) =>
    apiClient.delete(`/admin/campaigns/${id}`),

  // Admin - Static Pages
  adminGetStaticPages: () =>
    apiClient.get('/admin/static-pages'),
  adminGetStaticPage: (id: number) =>
    apiClient.get(`/admin/static-pages/${id}`),
  adminCreateStaticPage: (data: Record<string, unknown>) =>
    apiClient.post('/admin/static-pages', data),
  adminUpdateStaticPage: (id: number, data: Record<string, unknown>) =>
    apiClient.put(`/admin/static-pages/${id}`, data),
  adminDeleteStaticPage: (id: number) =>
    apiClient.delete(`/admin/static-pages/${id}`),

  // Admin - Menus
  adminGetMenus: () =>
    apiClient.get('/admin/menus'),
  adminUpdateMenu: (id: number, data: Record<string, unknown>) =>
    apiClient.put(`/admin/menus/${id}`, data),
  adminCreateMenu: (data: Record<string, unknown>) =>
    apiClient.post('/admin/menus', data),
  adminDeleteMenu: (id: number) =>
    apiClient.delete(`/admin/menus/${id}`),

  // Admin - Roles
  adminGetRoles: () =>
    apiClient.get('/admin/roles'),
  adminCreateRole: (data: Record<string, unknown>) =>
    apiClient.post('/admin/roles', data),
  adminUpdateRole: (id: number, data: Record<string, unknown>) =>
    apiClient.put(`/admin/roles/${id}`, data),
  adminDeleteRole: (id: number) =>
    apiClient.delete(`/admin/roles/${id}`),

  // Admin - Support Tickets
  adminGetTickets: (params?: Record<string, unknown>) =>
    apiClient.get('/admin/tickets', { params }),
  adminGetTicket: (id: number) =>
    apiClient.get(`/admin/tickets/${id}`),
  adminCreateTicket: (data: Record<string, unknown>) =>
    apiClient.post('/admin/tickets', data),
  adminUpdateTicketStatus: (id: number, status: string) =>
    apiClient.patch(`/admin/tickets/${id}/status`, { status }),
  adminReplyTicket: (id: number, data: Record<string, unknown>) =>
    apiClient.post(`/admin/tickets/${id}/reply`, data),
  adminDeleteTicket: (id: number) =>
    apiClient.delete(`/admin/tickets/${id}`),

  // Admin - Bank Accounts
  adminGetBankAccounts: () =>
    apiClient.get('/admin/bank-accounts'),
  adminCreateBankAccount: (data: Record<string, unknown>) =>
    apiClient.post('/admin/bank-accounts', data),
  adminUpdateBankAccount: (id: number, data: Record<string, unknown>) =>
    apiClient.put(`/admin/bank-accounts/${id}`, data),
  adminDeleteBankAccount: (id: number) =>
    apiClient.delete(`/admin/bank-accounts/${id}`),

  // Admin - Payment Rules
  adminGetPaymentRules: () =>
    apiClient.get('/admin/payment-rules'),
  adminCreatePaymentRule: (data: Record<string, unknown>) =>
    apiClient.post('/admin/payment-rules', data),
  adminUpdatePaymentRule: (id: number, data: Record<string, unknown>) =>
    apiClient.put(`/admin/payment-rules/${id}`, data),
  adminDeletePaymentRule: (id: number) =>
    apiClient.delete(`/admin/payment-rules/${id}`),
};

// ---- Storage URL Helper ----
export const storageUrl = (path: string): string => {
  const base = import.meta.env.VITE_STORAGE_URL || '';
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `${base}/${path.replace(/^\//, '')}`;
};

// ---- Health Check ----
export const checkApiHealth = async (): Promise<boolean> => {
  try {
    const res = await axios.get(`${API_BASE_URL?.replace('/v1', '')}/health`, { timeout: 5000 });
    return res.data?.status === 'ok';
  } catch {
    return false;
  }
};

export default apiClient;
